<script lang="ts">
  import { isAuthenticated, userName, clearSession, avatar, userScore } from './stores/auth';
  import { connect, disconnect, wsStatus } from './stores/ws';
  import { matchSnapshot, inMatch } from './stores/match';
  import { api, sessionExpiredFlag } from './lib/api';
  import { activeLobbyTab, loginModalOpen, openLogin } from './stores/uiState';
  import AvatarPicker from './components/avatar/AvatarPicker.svelte';
  import GameView2v2 from './components/game/GameView2v2.svelte';
  import RoundToast from './components/toasts/RoundToast.svelte';
  // Side-effect: subscribes em matchSnapshot pra disparar toasts.
  import './stores/narration';
  // Side-effect: subscribe em matchSnapshot pra alimentar
  // displayedMatchSnapshot (hold quando vaza fecha).
  import './stores/displayed';
  // Side-effect: registra subscribe em lastMessage pra dispatchar
  // queue:match_found / game:state.
  import { resyncCurrentGame } from './stores/lobby';
  import AuthView from './components/auth/AuthView.svelte';
  import Lobby from './components/lobby/Lobby.svelte';
  import SearchingModal from './components/lobby/SearchingModal.svelte';
  import GameView1v1 from './components/game/GameView1v1.svelte';
  import WaitingNextOpponent from './components/lobby/WaitingNextOpponent.svelte';
  import TournamentDetailModal from './components/lobby/TournamentDetailModal.svelte';
  import AlertModal from './components/common/AlertModal.svelte';
  import { waitingForNextRound, currentMatchTournament, bracketModalOpen, bracketTournament, championCelebration } from './stores/tournaments';
  import ChampionModal from './components/lobby/ChampionModal.svelte';
  import { isIdle } from './stores/idle';
  import { Trophy, BarChart3, LogIn } from 'lucide-svelte';

  // Conexao WS reage a 3 fatores:
  //   1. Auth: se nao autenticado, sempre disconnect
  //   2. Idle: se idle (3min sem atividade) E nao em partida, disconnect
  //      pra parar de consumir vCPU-sec do free tier do Cloud Run
  //   3. Caso contrario, connect
  // Detalhe: NAO desconecta durante partida — auto-play do timer perderia
  // turno do jogador (Felipe 2026-05-25).
  $effect(() => {
    if (!$isAuthenticated) {
      disconnect();
      return;
    }
    if ($isIdle && !$inMatch) {
      disconnect();
      return;
    }
    void connect();
  });

  // sessionExpired: backend rejeitou o token (cold start zerou as sessions
  // in-memory). clearSession ja foi chamado por triggerSessionExpired. Aqui
  // so limpamos o match (UI volta pra AuthView automaticamente via
  // $isAuthenticated=false) e sinalizamos pra AuthView mostrar o aviso.
  let sessionExpiredMsg = $state(false);
  $effect(() => {
    if ($sessionExpiredFlag) {
      matchSnapshot.set(null);
      sessionExpiredMsg = true;
      sessionExpiredFlag.set(false);
      // sessao expirou: reabre o modal de login pro user re-logar.
      loginModalOpen.set(true);
      // limpa o aviso apos uns segundos pra nao ficar permanente
      setTimeout(() => { sessionExpiredMsg = false; }, 6000);
    }
  });

  // Fecha o modal de login automaticamente quando o user loga com sucesso.
  $effect(() => {
    if ($isAuthenticated && $loginModalOpen) {
      loginModalOpen.set(false);
      sessionExpiredMsg = false;
    }
  });

  // Resync: quando WS abre (apos login ou reconnect), busca match
  // ativo. Cold start de Cloud Run / reconnect mid-partida
  // ressincronizam estado sem precisar do user clicar nada.
  $effect(() => {
    if ($wsStatus === 'open' && $isAuthenticated) {
      void resyncCurrentGame();
    }
  });

  // Resync ao VOLTAR PRO FOREGROUND: iOS/Android suspendem o WS em
  // background SEM fechar a conexao — eventos game:state enviados
  // nesse meio tempo somem e a mesa fica stale ("oponente jogou e a
  // carta nao apareceu", Leonardo 2026-06-11). O resync por wsStatus
  // nao cobre isso porque o status nunca saiu de 'open'. Ao ficar
  // visivel de novo, busca o estado atual via REST (idempotente).
  $effect(() => {
    if (!$isAuthenticated) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void resyncCurrentGame();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  });

  function handleLogout() {
    void clearSession();
    matchSnapshot.set(null);
  }

  async function handleAbandon() {
    if (!confirm('Abandonar a partida atual? Volta pro lobby.')) return;
    try {
      await api.post('/games/current/abandon');
    } catch (_) { /* idempotente — segue mesmo se falhar */ }
    matchSnapshot.set(null);
  }

  let pickerOpen = $state(false);
</script>

<!-- Topbar so aparece quando NAO esta em partida — durante a partida
     todos os controles viram um botaozinho flutuante minimalista no
     canto pra dar maximo espaco a mesa (request do user 2026-05-18).
     Estilo cartoon Felipe 2026-05-26: background creme, logo "TORNEIOS
     TRUCO" no centro, avatar+nick esq, decor interrogacao dos lados. -->
{#if !$inMatch}
  <header class="topbar">
    <!-- LINHA 1 (HERO): avatar+info | decor ? | LOGO GRANDE | decor ?! | sair -->
    <div class="hero-row">
      {#if $isAuthenticated}
        <button class="user-block" onclick={() => (pickerOpen = true)} title="trocar avatar">
          <div class="avatar-wrap">
            <img class="avatar-img" src={`/images/${$avatar}.webp`} alt={$avatar} />
            <span class="ws-dot" class:open={$wsStatus === 'open'}
              title={`WebSocket: ${$wsStatus}`}></span>
          </div>
          <div class="user-info">
            <span class="user-nick">{$userName || '—'}</span>
            <span class="user-score">
              <svg class="trophy-mini" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M6 4h12v4a6 6 0 0 1-12 0V4z" stroke="currentColor" stroke-width="2" fill="currentColor"/>
                <path d="M6 6H4a2 2 0 0 0 0 4h2M18 6h2a2 2 0 0 1 0 4h-2" stroke="currentColor" stroke-width="2"/>
                <path d="M10 14h4v3h2v2H8v-2h2v-3z" fill="currentColor"/>
              </svg>
              <strong>{$userScore || 1500}</strong>
              <span class="score-label">RANKING</span>
            </span>
          </div>
        </button>
      {:else}
        <span></span>
      {/if}

      <img class="decor decor-left" src="/images/decor-interrogacao.webp" alt="" aria-hidden="true" />
      <img class="logo-img" src="/images/logo-torneios.webp" alt="Torneios Truco" />
      <img class="decor decor-right" src="/images/decor-interrogacao-exclamacao.webp" alt="" aria-hidden="true" />

      {#if $isAuthenticated}
        <button class="logout-btn" onclick={handleLogout}>sair</button>
      {:else}
        <span></span>
      {/if}
    </div>

    <!-- LINHA 2: tabs Torneios / Ranking (+ Entrar quando deslogado) -->
    <nav class="header-tabs" role="tablist">
      <button
        role="tab"
        class="htab"
        aria-selected={$activeLobbyTab === 'torneios'}
        class:active={$activeLobbyTab === 'torneios'}
        onclick={() => activeLobbyTab.set('torneios')}
      ><Trophy size={14} strokeWidth={2.5} /> Torneios</button>
      <button
        role="tab"
        class="htab"
        aria-selected={$activeLobbyTab === 'ranking'}
        class:active={$activeLobbyTab === 'ranking'}
        onclick={() => activeLobbyTab.set('ranking')}
      ><BarChart3 size={14} strokeWidth={2.5} /> Ranking</button>
      {#if !$isAuthenticated}
        <button class="htab htab-enter" onclick={() => openLogin()}><LogIn size={14} strokeWidth={2.5} /> Entrar</button>
      {/if}
    </nav>
  </header>
{/if}

<main class:in-match={$inMatch}>
  {#if $inMatch && $matchSnapshot}
    {#if $matchSnapshot.format === '2v2'}
      <GameView2v2 snap={$matchSnapshot} />
    {:else}
      <GameView1v1 snap={$matchSnapshot} />
    {/if}
  {:else if $waitingForNextRound}
    <!-- Jogador venceu sua match do torneio mas a proxima fase ainda nao
         foi spawnada (sibling match em andamento). Tela de espera. -->
    <WaitingNextOpponent
      tournament={$waitingForNextRound.tournament}
      currentRoundIdx={$waitingForNextRound.roundIdx}
      playerMatchIdx={$waitingForNextRound.matchIdx}
    />
  {:else}
    <Lobby />
  {/if}
</main>

<!-- Durante partida: so a indicacao de offline (se desconectou). O botao
     de chaveamento foi movido pro broche 🏆 dentro do Scoreboard2v2
     (Felipe 2026-05-28: estava colidindo com o painel ELES). Botao
     "sair" removido pra evitar abandono acidental (Leonardo 2026-05-20). -->
{#if $isAuthenticated && $inMatch && $wsStatus !== 'open'}
  <div class="match-corner">
    <span class="ws-warn" title={`WebSocket: ${$wsStatus}`}>● offline</span>
  </div>
{/if}

{#if $bracketModalOpen && ($currentMatchTournament ?? $bracketTournament)}
  <TournamentDetailModal
    tournament={($currentMatchTournament ?? $bracketTournament)!}
    onClose={() => { bracketModalOpen.set(false); bracketTournament.set(null); }}
  />
{/if}

{#if $championCelebration}
  <ChampionModal tournament={$championCelebration} />
{/if}

{#if $loginModalOpen && !$isAuthenticated}
  <div class="login-overlay" role="dialog" aria-modal="true">
    <div class="login-shell">
      <button class="login-close" onclick={() => loginModalOpen.set(false)} aria-label="fechar">×</button>
      {#if sessionExpiredMsg}
        <div class="session-expired-banner" role="alert">sua sessão expirou — entre de novo</div>
      {/if}
      <AuthView />
    </div>
  </div>
{/if}

<SearchingModal />
<AvatarPicker open={pickerOpen} onClose={() => (pickerOpen = false)} />
<RoundToast />
<AlertModal />

<style>
  /* Topbar 2 linhas estilo mockup (Felipe + Leonardo 2026-05-26):
     LINHA 1: decor + LOGO grande + decor
     LINHA 2: avatar + nick + tabs Torneios/Ranking + sair */
  .topbar {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 14px 10px;
    background: linear-gradient(180deg, #faf5ec 0%, #fff 60%, #f0e7d5 100%);
    border-bottom: 4px solid #1565C0;
    /* fio dourado sob a borda azul — ecoa o outline #ffb300 da tabela
       de torneios, costurando header e corpo na mesma identidade */
    box-shadow:
      0 3px 0 #ffb300,
      0 6px 16px rgba(21, 101, 192, 0.25);
    position: sticky;
    top: 0;
    z-index: 100;
  }
  .hero-row {
    display: grid;
    grid-template-columns: auto auto 1fr auto auto;
    align-items: center;
    gap: 8px;
    /* Conteudo do header limitado + centralizado (fundo continua full-bleed)
       pra nao colar avatar/sair nas bordas em telas largas. Felipe 2026-05-27 */
    width: 100%;
    max-width: 1200px;
    margin-inline: auto;
  }

  /* User block (avatar + nick + score) - clicavel pra trocar avatar */
  .user-block {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 4px 8px 4px 4px;
    background: transparent;
    border: none;
    cursor: pointer;
    border-radius: 12px;
    transition: background 0.15s;
  }
  .user-block:hover { background: rgba(21, 101, 192, 0.08); }
  .avatar-wrap { position: relative; flex-shrink: 0; }
  .user-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    line-height: 1;
  }
  .user-nick {
    font-size: 18px;
    font-weight: 900;
    color: #1565C0;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);
  }
  .user-score {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: #f9a825;
    font-weight: 800;
  }
  .trophy-mini { width: 14px; height: 14px; }
  .user-score strong { font-size: 16px; color: #0D47A1; }
  .score-label {
    font-size: 9px;
    color: #1976D2;
    letter-spacing: 1.5px;
    margin-left: 2px;
  }

  .decor {
    width: 56px;
    height: 56px;
    object-fit: contain;
    pointer-events: none;
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.18));
    flex-shrink: 0;
  }
  .decor-left { transform: rotate(-8deg); }
  .decor-right { transform: rotate(8deg); }

  .logo-img {
    height: 90px;
    max-width: 100%;
    width: auto;
    object-fit: contain;
    filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.3));
    justify-self: center;
  }

  /* Tabs no header em linha separada */
  .header-tabs {
    display: flex;
    gap: 6px;
    padding: 4px;
    background: rgba(21, 101, 192, 0.08);
    border: 2px solid #1565C0;
    border-radius: 10px;
    box-shadow: 0 2px 0 #0D47A1;
    width: 100%;
    max-width: 1200px;
    margin: 4px auto 0;
    box-sizing: border-box;
  }
  .htab {
    flex: 1;
    padding: 8px 14px;
    background: transparent;
    border: none;
    color: #1565C0;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 1px;
    text-transform: uppercase;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.12s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    /* permite a tab ENCOLHER no flex (default min-width:auto estoura
       o container com 3 tabs + icones em tela estreita) */
    min-width: 0;
    white-space: nowrap;
  }
  .htab :global(svg) { flex-shrink: 0; }
  .htab:hover { background: rgba(21, 101, 192, 0.12); }
  .htab.active {
    background: linear-gradient(180deg, #1976D2 0%, #1565C0 100%);
    color: #fff;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
    box-shadow: inset 0 -2px 0 #0D47A1;
  }
  /* 3a tab "Entrar" (login) — destaque verde pra chamar atencao. */
  .htab-enter {
    color: #fff;
    background: linear-gradient(180deg, #66bb6a, #43a047 60%, #2e7d32);
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
    box-shadow: inset 0 -2px 0 #1b5e20;
  }
  .htab-enter:hover { background: linear-gradient(180deg, #81c784, #66bb6a 60%, #43a047); }

  .user-name {
    font-size: 12px;
    color: #0D47A1;
    font-weight: 800;
    letter-spacing: 0.3px;
  }
  .logout-btn {
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 700;
    background: #fff;
    color: #1565C0;
    border: 2px solid #1565C0;
    border-radius: 999px;
    box-shadow: 0 2px 0 #0D47A1;
    transition: all 0.1s;
  }
  .logout-btn:hover {
    background: #e3f2fd;
    transform: translateY(-1px);
    box-shadow: 0 3px 0 #0D47A1;
  }
  .logout-btn:active {
    transform: translateY(1px);
    box-shadow: 0 1px 0 #0D47A1;
  }

  /* Botao ENTRAR (quando nao logado) — destaque verde cartoon */
  .enter-btn {
    padding: 9px 20px;
    font-size: 13px;
    font-weight: 900;
    letter-spacing: 1px;
    color: #fff;
    background: linear-gradient(180deg, #66bb6a, #43a047 55%, #2e7d32);
    border: 2px solid #1b5e20;
    border-radius: 999px;
    box-shadow: 0 3px 0 #1b5e20, inset 0 1px 0 rgba(255, 255, 255, 0.35);
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
    cursor: pointer;
    transition: transform 0.1s, box-shadow 0.1s;
    white-space: nowrap;
  }
  .enter-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 0 #1b5e20, inset 0 1px 0 rgba(255,255,255,0.4); }
  .enter-btn:active { transform: translateY(2px); box-shadow: 0 1px 0 #1b5e20; }

  /* Overlay do modal de login (on-demand) */
  .login-overlay {
    position: fixed;
    inset: 0;
    z-index: 1250;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: radial-gradient(circle at 50% 30%, rgba(21, 101, 192, 0.7), rgba(2, 3, 26, 0.92));
    backdrop-filter: blur(6px);
    overflow-y: auto;
  }
  .login-shell {
    position: relative;
    width: 100%;
    max-width: 380px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }
  .login-close {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 3;
    width: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    border: 2px solid #1565C0;
    background: #fff;
    color: #1565C0;
    font-size: 20px;
    line-height: 1;
    font-weight: 900;
    cursor: pointer;
    box-shadow: 0 2px 0 #0D47A1;
  }
  .login-close:hover { background: #e3f2fd; }
  .login-close:active { transform: translateY(1px); box-shadow: 0 1px 0 #0D47A1; }

  /* Avatar chip cartoon */
  .avatar-chip {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: transparent;
    border: none;
    cursor: pointer;
  }
  .avatar-img {
    width: 52px;
    height: 52px;
    border-radius: 12px;
    object-fit: cover;
    border: 3px solid #ffd24a;
    box-shadow:
      0 0 0 3px #1565C0,
      0 3px 0 #0D47A1,
      0 5px 12px rgba(0, 0, 0, 0.25);
    background: #faf5ec;
    transition: transform 0.15s;
  }
  .avatar-chip:hover .avatar-img {
    transform: translateY(-2px) scale(1.03);
  }
  .avatar-chip:active .avatar-img {
    transform: translateY(1px) scale(0.97);
  }
  .ws-dot {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #e53935;
    border: 2px solid #fff;
    box-shadow: 0 0 6px rgba(229, 57, 53, 0.6);
    transition: all 0.2s;
  }
  .ws-dot.open {
    background: #2e7d32;
    box-shadow: 0 0 8px rgba(46, 125, 50, 0.7);
  }

  @media (max-width: 640px) {
    .topbar { padding: 8px 10px 10px; gap: 6px; }
    .hero-row {
      grid-template-columns: auto 1fr auto;
    }
    .hero-row .decor-left,
    .hero-row .decor-right { display: none; }
    .decor { width: 40px; height: 40px; }
    .logo-img { height: 100px; }
    .user-nick { font-size: 13px; }
    .user-score strong { font-size: 13px; }
    .score-label { display: none; }
    .avatar-img { width: 60px; height: 60px; }
    .htab { font-size: 11px; padding: 6px 6px; letter-spacing: 0.4px; gap: 4px; }
    .htab :global(svg) { width: 12px; height: 12px; }
  }
  @media (max-width: 400px) {
    .user-info { display: none; }
    .logo-img { height: 86px; }
    .avatar-img { width: 56px; height: 56px; }
    /* tela mini: icone fica (11px) — quem paga a conta e o
       letter-spacing zerado + padding minimo. 3 tabs cabem ate 320px. */
    .htab { font-size: 10px; letter-spacing: 0; padding: 6px 2px; gap: 3px; }
    .htab :global(svg) { width: 11px; height: 11px; }
  }

  .logout-btn { padding: 6px 12px; font-size: 12px; }
  .abandon-btn {
    padding: 6px 12px;
    font-size: 12px;
    background: rgba(255, 76, 126, 0.12);
    border-color: rgba(255, 76, 126, 0.4);
    color: var(--danger);
  }
  .abandon-btn:hover {
    background: rgba(255, 76, 126, 0.2);
    border-color: rgba(255, 76, 126, 0.6);
  }

  main {
    flex: 1;
    min-height: 0; /* permite flex shrink + scroll interno */
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 18px 14px;
  }
  /* Palco do lobby (NUNCA em partida): glow azul descendo do header +
     calor magenta subindo de baixo — preenche o vazio escuro sem
     competir com a tabela. Game/fila intocados. */
  main:not(.in-match) {
    background:
      radial-gradient(ellipse 95% 55% at 50% -8%, rgba(25, 118, 210, 0.20) 0%, transparent 62%),
      radial-gradient(ellipse 75% 45% at 50% 115%, rgba(216, 80, 240, 0.08) 0%, transparent 60%);
  }
  /* Durante partida: layout fixo em viewport (100dvh), sem scroll.
   * Felipe 2026-05-21: desktop estava esticado/quebrado — constrange
   * a area de jogo a um shell tipo mobile (max-width 480px) e centra.
   * Em mobile (<= 520px) o max-width nao limita, layout fica fluido. */
  main.in-match {
    height: 100dvh;
    max-height: 100dvh;
    padding: 8px 6px 10px;
    overflow: hidden;
    align-items: center;
  }
  main.in-match :global(> *) {
    width: 100%;
    max-width: 480px;
  }

  /* Corner flutuante durante partida — minimalista, top-right. */
  .match-corner {
    position: fixed;
    top: 10px;
    right: 12px;
    z-index: 200;
    display: flex;
    align-items: center;
    gap: 6px;
    pointer-events: none;
  }
  .match-corner > * { pointer-events: auto; }
  .ws-warn {
    font-size: 9px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--danger);
    background: rgba(2, 3, 26, 0.85);
    padding: 4px 8px;
    border-radius: 999px;
    border: 1px solid rgba(255, 76, 126, 0.45);
    font-family: ui-monospace, monospace;
  }
  .abandon-pill {
    padding: 5px 14px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    background: rgba(255, 76, 126, 0.12);
    border: 1px solid rgba(255, 76, 126, 0.4);
    color: var(--danger);
    border-radius: 999px;
    backdrop-filter: blur(6px);
    transition: background 0.15s, border-color 0.15s;
  }
  .abandon-pill:hover {
    background: rgba(255, 76, 126, 0.22);
    border-color: rgba(255, 76, 126, 0.65);
  }

  .game-placeholder {
    text-align: center;
    max-width: 480px;
    padding-top: 30px;
  }
  .game-placeholder h2 { margin-bottom: 12px; }
  .muted { color: var(--text-dim); margin: 4px 0; }
  .small { font-size: 12px; margin-top: 14px; }
  code {
    background: var(--surface);
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid var(--border);
    font-size: 11px;
  }
  .back-btn { margin-top: 16px; }
  .session-expired-banner {
    margin: 0 auto 14px;
    max-width: 480px;
    padding: 10px 16px;
    background: rgba(255, 76, 126, 0.14);
    border: 1px solid rgba(255, 76, 126, 0.55);
    color: var(--danger);
    border-radius: 8px;
    font-size: 13px;
    text-align: center;
    letter-spacing: 0.5px;
  }
</style>
